import intlTelInput from 'intl-tel-input'

class Forms {
  constructor (prefix) {
    this.prefix = prefix
    this.phoneInput = null
    this.iti = null
    this.updateLabelBound = null
  }

  init (formEl = null) {
    const prefix = this.prefix

    // Usar el elemento pasado directamente, o buscar el primero visible
    const section = formEl || [...document.querySelectorAll(prefix)]
      .find(el => el.offsetParent !== null)

    if (!section) return

    const form = section.querySelector('form')
    if (form === null) return

    // Cargar reCAPTCHA solo cuando el usuario interactúa
    const loadRecaptchaOnInteraction = () => {
      this.loadScript()
      // Eliminar listeners una vez cargado
      form.removeEventListener('focusin', loadRecaptchaOnInteraction)
      form.removeEventListener('touchstart', loadRecaptchaOnInteraction)
    }

    form.addEventListener('focusin', loadRecaptchaOnInteraction)
    form.addEventListener('touchstart', loadRecaptchaOnInteraction)

    // Inicializar intl-tel-input
    const phoneInput = form.querySelector('.tlf-intl')
    let iti = null
    if (phoneInput) {
      phoneInput.removeAttribute('pattern')
      phoneInput.removeAttribute('maxlength')
      phoneInput.type = 'tel'

      iti = intlTelInput(phoneInput, {
        initialCountry: 'es',
        preferredCountries: ['es', 'fr', 'pt', 'it', 'de', 'gb'],
        separateDialCode: true,
        utilsScript: '/js/intl-tel-input/utils.js',
        customPlaceholder: function (selectedCountryPlaceholder, selectedCountryData) {
          return selectedCountryPlaceholder
        }
      })

      // Path banderas en una ruta específica
      setTimeout(() => {
        const style = document.createElement('style')
        style.textContent = `
          .iti__flag {
            background-image: url("/wp-content/themes/wpt/images/intl-tel-input/flags.webp");
          }
          @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
            .iti__flag {
              background-image: url("/wp-content/themes/wpt/images/intl-tel-input/flags@2x.webp");
            }
          }
        `
        document.head.appendChild(style)
      }, 100)

      // Traducir Search
      const translateSearchPlaceholder = () => {
        const inputs = document.querySelectorAll('input')
        inputs.forEach(input => {
          if (input.placeholder === 'Search' || input.classList.contains('iti__search-input')) {
            input.placeholder = 'Buscar país...'
          }
        })
      }

      setTimeout(translateSearchPlaceholder, 100)

      phoneInput.addEventListener('focus', translateSearchPlaceholder)
      phoneInput.addEventListener('click', translateSearchPlaceholder)

      if (iti) {
        phoneInput.addEventListener('countrychange', translateSearchPlaceholder)
      }

      let intervalCount = 0
      const intervalId = setInterval(() => {
        translateSearchPlaceholder()
        intervalCount++
        if (intervalCount >= 5) {
          clearInterval(intervalId)
        }
      }, 2000)

      const phoneContainer = phoneInput.closest('.iti') || phoneInput.parentElement
      if (phoneContainer) {
        const observer = new MutationObserver(() => { // eslint-disable-line
          translateSearchPlaceholder()
        })
        observer.observe(phoneContainer, {
          childList: true,
          subtree: true
        })
      }

      this.setupPhoneLabel(phoneInput, iti)
    }

    // Evento submit del formulario
    form.addEventListener('submit', (e) => {
      e.preventDefault()

      const requiredInputs = form.querySelectorAll('[required]')
      let allFilled = true

      requiredInputs.forEach(input => {
        if (!input.value.trim()) {
          allFilled = false
          input.closest('div').classList.add('hasError')
        } else {
          input.closest('div').classList.remove('hasError')
        }
      })

      if (!allFilled) return

      let finalPhoneNumber = phoneInput ? phoneInput.value : ''

      if (iti && phoneInput && phoneInput.value) {
        const formattedNumber = iti.getNumber()

        if (formattedNumber && formattedNumber.startsWith('+')) {
          finalPhoneNumber = formattedNumber
        } else {
          const countryData = iti.getSelectedCountryData()
          if (countryData && countryData.dialCode) {
            const cleanNumber = phoneInput.value.replace(/\D/g, '')
            if (!cleanNumber.startsWith(countryData.dialCode)) {
              finalPhoneNumber = '+' + countryData.dialCode + cleanNumber
            }
          }
        }

        phoneInput.value = finalPhoneNumber
      }

      // Obtener campos con validación de existencia
      const fields = {
        name: e.target.elements.name,
        phone: e.target.elements.phone,
        email: e.target.elements.email,
        zip: e.target.elements.zip,
        order_id: e.target.elements.order_id || null,
        privacity: e.target.elements.privacity,
        form_id: e.target.elements.form_id
      }

      // Verificar que los campos obligatorios existan
      if (!fields.name || !fields.email || !fields.phone || !fields.zip) {
        return
      }

      const data = {
        form_id: fields.form_id,
        name: fields.name,
        email: fields.email,
        zip: fields.zip,
        order_id: fields.order_id,
        privacity: fields.privacity,
        phone: { value: finalPhoneNumber }
      }

      const onSuccess = () => this.showSuccess(section)

      this.showLoading(section)
      this.postForm(data, onSuccess)
    })
  }

  setupPhoneLabel (phoneInput, iti) {
    // Buscar el label dentro del formulario específico, no globalmente
    const form = phoneInput.closest('form')
    let label = form ? form.querySelector('label[for="phone"]') : null

    if (!label) {
      label = document.querySelector('label[for="phone"]')
    }

    if (!label) {
      return
    }

    // Guardamos referencias para poder remover listeners al resetear
    this.phoneInput = phoneInput
    this.iti = iti

    const updateLabel = () => {
      const hasValue = phoneInput.value.trim() !== ''
      const isFocused = document.activeElement === phoneInput
      const isValidFormat = this.isValidPhoneFormat(phoneInput.value)

      requestAnimationFrame(() => { // eslint-disable-line
        label.classList.remove('label-active', 'label-valid', 'label-invalid')

        if (hasValue) {
          label.classList.add('label-active')
          if (!isFocused) {
            if (isValidFormat) {
              label.classList.add('label-valid')
            } else {
              label.classList.add('label-invalid')
            }
          }
          return
        }

        if (isFocused) {
          label.classList.add('label-active')
        }
      })
    }

    // Guardamos la referencia del listener para poder eliminarla después
    this.updateLabelBound = updateLabel

    phoneInput.addEventListener('focus', updateLabel)
    phoneInput.addEventListener('blur', updateLabel)
    phoneInput.addEventListener('input', updateLabel)
    phoneInput.addEventListener('change', updateLabel)

    if (iti) {
      phoneInput.addEventListener('countrychange', updateLabel)
    }

    // Llamada inicial retardada para que intl-tel-input ya haya inicializado el dial code
    setTimeout(updateLabel, 500)
  }

  removePhoneListeners () {
    if (!this.phoneInput || !this.updateLabelBound) return

    this.phoneInput.removeEventListener('focus', this.updateLabelBound)
    this.phoneInput.removeEventListener('blur', this.updateLabelBound)
    this.phoneInput.removeEventListener('input', this.updateLabelBound)
    this.phoneInput.removeEventListener('change', this.updateLabelBound)

    if (this.iti) {
      this.phoneInput.removeEventListener('countrychange', this.updateLabelBound)
    }

    this.updateLabelBound = null
  }

  isValidPhoneFormat (phone) {
    if (!phone || phone.trim() === '') {
      return false
    }

    const cleanPhone = phone.replace(/[^\d+]/g, '')
    const digitsOnly = cleanPhone.replace('+', '')

    if (cleanPhone.startsWith('+')) {
      return digitsOnly.length >= 8
    }

    return digitsOnly.length >= 9
  }

  loadScript () {
    if (window.grecaptcha === undefined) {
      const script = document.createElement('script')
      script.setAttribute(
        'src',
        'https://www.google.com/recaptcha/api.js?render=' +
          window.scripts_ajax_variables.recaptcha_public
      )
      script.async = true
      script.defer = true
      document.getElementsByTagName('head')[0].appendChild(script)
    }
  }

  postForm (data, onSuccess) {
    const action = 'send_form'

    // Verificar si reCAPTCHA está configurado
    const recaptchaPublic = window.scripts_ajax_variables?.recaptcha_public
    if (!recaptchaPublic || recaptchaPublic === '') {
      this.sendAjaxRequest(data, action, onSuccess)
      return
    }

    if (typeof window.grecaptcha === 'undefined') {
      this.sendAjaxRequest(data, action, onSuccess)
      return
    }

    const executeRecaptcha = () => {
      window.grecaptcha.ready(function () {
        window.grecaptcha
          .execute(recaptchaPublic, { action: action })
          .then(function (token) {
            this.sendAjaxRequest(data, action, onSuccess, token)
          }.bind(this))
          .catch(function () {
            this.sendAjaxRequest(data, action, onSuccess)
          }.bind(this))
      }.bind(this))
    }

    if (typeof window.grecaptcha.execute === 'undefined') {
      let elapsed = 0
      const waitForReady = setInterval(() => {
        elapsed += 100
        if (typeof window.grecaptcha.execute !== 'undefined') {
          clearInterval(waitForReady)
          executeRecaptcha()
        } else if (elapsed >= 5000) {
          clearInterval(waitForReady)
          this.sendAjaxRequest(data, action, onSuccess)
        }
      }, 100)
      return
    }

    executeRecaptcha()
  }

  sendAjaxRequest (data, action, onSuccess, token = '') {
    const xmlhttp = window.ajaxReq()
    const url = encodeURI(window.scripts_ajax_variables.ajax_url)

    let params = 'action=' + action
    params += '&formFromUrl=' + window.location.pathname
    params += '&form_id=' + (data.form_id ? data.form_id.value : '')
    params += '&name=' + (data.name ? encodeURIComponent(data.name.value) : '')
    params += '&zip=' + (data.zip ? encodeURIComponent(data.zip.value) : '')
    params += '&email=' + (data.email ? encodeURIComponent(data.email.value) : '')
    params += '&phone=' + encodeURIComponent(data.phone.value)
    params += '&order_id=' + (data.order_id ? encodeURIComponent(data.order_id.value) : '')
    params += '&privacity=' + (data.privacity ? (data.privacity.checked ? 'true' : 'false') : 'false')
    params += '&pageName=' + encodeURIComponent(document.title)
    params += '&nonce=' + window.scripts_ajax_variables.nonce
    params += '&token=' + token
    params = params.replaceAll('=null&', '=&')

    xmlhttp.open('POST', url, true)
    xmlhttp.setRequestHeader(
      'Content-type',
      'application/x-www-form-urlencoded'
    )

    xmlhttp.onreadystatechange = function () {
      if (xmlhttp.readyState === 4) {
        if (xmlhttp.status === 200) {
          try {
            const responseData = JSON.parse(xmlhttp.responseText)
            if (responseData.success === true) {
              onSuccess()
            }
          } catch (error) {
            // Error silencioso en producción
          }
        }
      }
    }

    xmlhttp.send(params)
  }

  showLoading (section) {
    const prefix = this.prefix

    const formEl = section.querySelector('' + prefix + '__form')
    const titleEl = section.querySelector('' + prefix + '__title')
    const descriptionEl = section.querySelector('' + prefix + '__description')
    const loadingEl = section.querySelector('' + prefix + '__loading')

    if (formEl) formEl.classList.add('isHidden')
    if (titleEl) titleEl.classList.add('isHidden')
    if (descriptionEl) descriptionEl.classList.add('isHidden')
    if (loadingEl) loadingEl.classList.remove('isHidden')
  }

  showSuccess (section) {
    const prefix = this.prefix

    const form = document.querySelector(prefix)
    const idform = form ? form.getElementsByTagName('form')[0]?.id : ''

    const loadingEl = section.querySelector('' + prefix + '__loading')
    const successEl = section.querySelector('' + prefix + '__success')

    if (loadingEl) loadingEl.classList.add('isHidden')
    if (successEl) successEl.classList.remove('isHidden')

    // Obtener el order_id si existe
    const orderId = document.querySelector('#' + idform + ' ' + 'input[name=order_id]')?.value || null

    // Enviar evento al DataLayer solo con order_id
    if (typeof window.dataLayer !== 'undefined') {
      window.dataLayer.push({
        event: 'form_submission_complete',
        order_data: {
          order_id: orderId
        },
        timestamp: new Date().toISOString()
      })
    }

    // Enviar evento a Google Analytics
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'lead', {
        event_category: 'formulario',
        event_label: 'contactform',
        order_id: orderId
      })
    }
  }
}

export default Forms
